import { useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import LandingHeader from '../components/organisms/LandingHeader';
import Footer from '../components/organisms/Footer';
import Input from '../components/atoms/Input';
import Button from '../components/atoms/Button';
import { getCourses } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import SessionManager from '../utils/sessionManager';

// Animated Counter Component
const AnimatedCounter = ({ end, suffix = '', duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const counterRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (counterRef.current) {
      observer.observe(counterRef.current);
    }

    return () => {
      if (counterRef.current) {
        observer.unobserve(counterRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    let startTime;
    let animationFrame;

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * end));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [isVisible, end, duration]);

  return (
    <div ref={counterRef} className="text-4xl font-bold text-blue-600">
      {count}{suffix}
    </div>
  );
};

const categoryFilters = [
  'Popular',
  'Business',
  'Computer Science',
  'Data Science',
  'Health',
  'Information Technology',
  'Arts and Humanities',
];

const testimonials = [
  {
    name: 'Priya Sharma',
    role: 'Software Engineer, Bangalore',
    initial: 'P',
    bgColor: 'bg-blue-600',
    text: 'I have a full-time job and family responsibilities. I needed the flexibility offered by this platform in order to achieve my goals. The AI-powered learning motivated me to keep progressing.',
  },
  {
    name: 'Rajesh Kumar',
    role: 'Data Analyst, Mumbai',
    initial: 'R',
    bgColor: 'bg-purple-600',
    text: 'The platform keeps me motivated to learn. With each course, I\'m getting more value out of my subscription. I can access almost anything with the courses available.',
  },
  {
    name: 'Ananya Desai',
    role: 'Product Manager, Hyderabad',
    initial: 'A',
    bgColor: 'bg-pink-600',
    text: 'I really appreciate the flexibility I get with my subscription. I can try any course and switch to another one for no additional cost. This motivates me to learn even more!',
  },
];

const companyLogos = [
  { name: 'Google', logo: 'https://upload.wikimedia.org/wikipedia/commons/2/2f/Google_2015_logo.svg' },
  { name: 'IBM', logo: 'https://upload.wikimedia.org/wikipedia/commons/5/51/IBM_logo.svg' },
  { name: 'Microsoft', logo: 'https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg' },
  { name: 'Amazon', logo: 'https://upload.wikimedia.org/wikipedia/commons/a/a9/Amazon_logo.svg' },
  { name: 'Meta', logo: 'https://upload.wikimedia.org/wikipedia/commons/7/7b/Meta_Platforms_Inc._logo.svg' },
];

const careerSkills = [
  {
    title: 'Python for Everybody',
    provider: 'University of Michigan',
    level: 'Beginner',
    type: 'Specialization',
    image: 'https://images.unsplash.com/photo-1526379095098-d400fd0bf935?w=400&h=250&fit=crop',
  },
  {
    title: 'Prompt Engineering',
    provider: 'Vanderbilt University',
    level: 'Beginner',
    type: 'Specialization',
    image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=400&h=250&fit=crop',
    badge: 'AI skills',
  },
  {
    title: 'IBM Data Science',
    provider: 'IBM',
    level: 'Beginner',
    type: 'Professional Certificate',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=250&fit=crop',
  },
];

const investBenefits = [
  {
    icon: (
      <svg className="w-16 h-16 text-pink-500" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z"/>
      </svg>
    ),
    title: 'Explore new skills',
    description: 'Access 10,000+ courses in AI, business, technology, and more.',
  },
  {
    icon: (
      <svg className="w-16 h-16 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
        <path d="M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82zM12 3L1 9l11 6 9-4.91V17h2V9L12 3z"/>
      </svg>
    ),
    title: 'Earn valuable credentials',
    description: 'Get certificates for every course you finish and boost your chances of getting hired after your trial ends at no additional cost.',
  },
  {
    icon: (
      <svg className="w-16 h-16 text-yellow-500" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
      </svg>
    ),
    title: 'Learn from the best',
    description: 'Take your skills to the next level with expert-led courses and AI-powered guide.',
  },
];

const CoursesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoggedIn, role } = useAuth();
  const [activeCategory, setActiveCategory] = useState('Popular');
  const [searchQuery, setSearchQuery] = useState('');
  const [apiCourses, setApiCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrolledCourseIds, setEnrolledCourseIds] = useState([]);
  const [hasUsedFreeTrial, setHasUsedFreeTrial] = useState(true); // default true to hide until checked
  
  useEffect(() => {
    const loadEnrolledCourses = async () => {
      if (isLoggedIn() && role === 'candidate') {
        try {
          const candidateSession = JSON.parse(localStorage.getItem('candidate_session') || '{}');
          const token = candidateSession?.access_token;
          
          if (token) {
            const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://aptirix-version-610696728606.asia-south1.run.app/api'}/course-teaching/my-courses`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (response.ok) {
              const data = await response.json();
              const enrolledIds = (data.courses || []).map(c => c.id);
              setEnrolledCourseIds(enrolledIds);
            }
          }
        } catch (error) {
          console.error('Error loading enrolled courses:', error);
        }
      }
    };
    
    loadEnrolledCourses();

    // Check free trial status from DB
    const checkFreeTrial = async () => {
      if (isLoggedIn() && role === 'candidate') {
        try {
          const session = SessionManager.getSession('candidate');
          const candidateId = session?.user_id;
          const token = session?.access_token;
          if (candidateId && token) {
            const res = await fetch(`${process.env.REACT_APP_API_URL || 'https://aptirix-version-610696728606.asia-south1.run.app/api'}/candidates/${candidateId}/free-trial-status`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
              const data = await res.json();
              setHasUsedFreeTrial(data.free_trial_used === true);
            } else {
              setHasUsedFreeTrial(false);
            }
          } else {
            setHasUsedFreeTrial(false);
          }
        } catch {
          setHasUsedFreeTrial(false);
        }
      } else {
        setHasUsedFreeTrial(false);
      }
    };
    checkFreeTrial();
  }, [isLoggedIn, role]);
  
  useEffect(() => {
    const loadCourses = async () => {
      try {
        const courses = await getCourses();
        setApiCourses(courses);
      } catch (error) {
        console.error('Error loading courses:', error);
      } finally {
        setLoading(false);
      }
    };
    loadCourses();
  }, []);
  
  useEffect(() => {
    if (location.state?.searchQuery) {
      setSearchQuery(location.state.searchQuery);
    }
  }, [location.state]);
  
  const filteredCourses = useMemo(() => {
    // Default course images based on category
    const getCourseImage = (category, index) => {
      const images = {
        'IT & Technology': [
          'https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=400&h=250&fit=crop',
          'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=400&h=250&fit=crop',
          'https://images.unsplash.com/photo-1484417894907-623942c8ee29?w=400&h=250&fit=crop'
        ],
        'Business': [
          'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=400&h=250&fit=crop',
          'https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=400&h=250&fit=crop',
          'https://images.unsplash.com/photo-1553877522-43269d4ea984?w=400&h=250&fit=crop'
        ],
        'Computer Science': [
          'https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=400&h=250&fit=crop',
          'https://images.unsplash.com/photo-1515879218367-8466d910aaa4?w=400&h=250&fit=crop',
          'https://images.unsplash.com/photo-1550439062-609e1531270e?w=400&h=250&fit=crop'
        ],
        'Data Science': [
          'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=250&fit=crop',
          'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=250&fit=crop',
          'https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=400&h=250&fit=crop'
        ],
        'Health': [
          'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=400&h=250&fit=crop',
          'https://images.unsplash.com/photo-1505751172876-fa1923c5c528?w=400&h=250&fit=crop',
          'https://images.unsplash.com/photo-1584982751601-97dcc096659c?w=400&h=250&fit=crop'
        ],
        'Information Technology': [
          'https://images.unsplash.com/photo-1488590528505-98d2b5aba04b?w=400&h=250&fit=crop',
          'https://images.unsplash.com/photo-1518770660439-4636190af475?w=400&h=250&fit=crop',
          'https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=400&h=250&fit=crop'
        ],
        'Arts and Humanities': [
          'https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=400&h=250&fit=crop',
          'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=400&h=250&fit=crop',
          'https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?w=400&h=250&fit=crop'
        ]
      };
      
      const categoryImages = images[category] || images['IT & Technology'];
      return categoryImages[index % categoryImages.length];
    };
    
    const formattedApiCourses = apiCourses.map((course, index) => {
      // Calculate original price if not present (33% discount)
      const originalPrice = course.original_price || Math.round(course.price / (1 - 0.33));
      
      return {
        id: course.id,
        category: course.category,
        title: course.title,
        rating: course.rating || 4.5,
        reviews_count: course.reviews_count || 0,
        students: course.students_enrolled || 0,
        duration: `${course.duration_weeks} weeks`,
        level: course.level.charAt(0).toUpperCase() + course.level.slice(1),
        highlights: ['Online Learning', 'Certificate', 'Expert Instructors'],
        price: `₹${course.price.toLocaleString('en-IN')}`,
        original_price: originalPrice,
        provider: course.provider,
        description: course.description,
        image: course.thumbnail || getCourseImage(course.category, index)
      };
    });
    
    let courses = formattedApiCourses;
    
    // Filter by category
    if (activeCategory !== 'Popular') {
      courses = courses.filter((course) => course.category === activeCategory);
    }
    
    // Apply search filter if search query exists
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      courses = courses.filter((course) => 
        course.title.toLowerCase().includes(query) ||
        (course.description && course.description.toLowerCase().includes(query)) ||
        (course.provider && course.provider.toLowerCase().includes(query))
      );
    }
    
    return courses;
  }, [searchQuery, apiCourses, activeCategory]);

  const requireAuth = () => {
    // Double-check with SessionManager directly
    const candidateSession = SessionManager.getSession('candidate');
    const hasValidSession = candidateSession && candidateSession.access_token;
    
    console.log('🔐 requireAuth check:', { 
      isLoggedInHook: isLoggedIn(), 
      roleFromHook: role,
      hasValidSession,
      candidateSessionExists: !!candidateSession
    });
    
    // Check if logged in and is a candidate
    if (!hasValidSession) {
      console.log('❌ No valid candidate session, redirecting to login');
      navigate('/login');
      return false;
    }
    
    console.log('✅ Auth check passed');
    return true;
  };

  const handleEnroll = (course) => {
    if (requireAuth()) {
      if (enrolledCourseIds.includes(course.id)) {
        navigate('/my-courses');
        return;
      }
      
      // Navigate to billing page
      navigate(`/course-billing/${course.id}`);
    }
  };
  
  const isEnrolled = (courseId) => {
    return enrolledCourseIds.includes(courseId);
  };

  return (
    <div className="relative flex min-h-screen flex-col bg-white">
      <LandingHeader />
      
      <main>
        {/* Hero Section with Free Trial - hidden after first use */}
        {!hasUsedFreeTrial && <section className="bg-gradient-to-br from-blue-50 via-white to-purple-50 py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid items-center gap-12 lg:grid-cols-2">
              <div>
                <div className="inline-block rounded-full bg-blue-600 px-4 py-1 text-sm font-semibold text-white mb-4">
                  APTIRIX PLUS
                </div>
                <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl lg:text-6xl">
                  Achieve your career goals with AI-Powered Learning
                </h1>
                <p className="mt-6 text-lg text-gray-600">
                  Build job-ready skills with industry-relevant courses designed for Indian professionals.
                </p>
                <div className="mt-8">
                  <div className="flex flex-wrap gap-4">
                    <Button 
                      className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 text-lg rounded-lg"
                      onClick={() => { if (requireAuth()) { navigate('/course-preview'); } }}
                    >
                      Start Free Trial
                    </Button>
                    <Button 
                      className="bg-white hover:bg-gray-50 text-blue-600 border-2 border-blue-600 px-8 py-3 text-lg rounded-lg"
                      onClick={() => navigate('/all-courses')}
                    >
                      View Courses
                    </Button>
                  </div>
                  <p className="mt-3 text-sm text-gray-600">
                    Experience AI-powered learning at no cost
                  </p>
                </div>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600&h=400&fit=crop"
                  alt="Learning"
                  className="rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>}

        {/* Why Choose Aptirix Section */}
        <section className="bg-white py-16 border-b">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <h2 className="text-center text-3xl font-bold text-gray-900 mb-12">
              Why Choose Aptirix for Learning?
            </h2>
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4 mb-12">
              <div className="bg-white rounded-xl border-2 border-gray-200 p-6 text-center hover:shadow-lg transition">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">AI-Powered Learning</h3>
                <p className="text-gray-600 text-sm">Personalized course recommendations and adaptive learning paths</p>
                <button className="mt-4 text-blue-600 text-sm font-medium hover:underline">Learn More</button>
              </div>
              <div className="bg-white rounded-xl border-2 border-gray-200 p-6 text-center hover:shadow-lg transition">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Industry-Ready Skills</h3>
                <p className="text-gray-600 text-sm">Courses designed with input from top Indian companies and recruiters</p>
                <button className="mt-4 text-blue-600 text-sm font-medium hover:underline">Learn More</button>
              </div>
              <div className="bg-white rounded-xl border-2 border-gray-200 p-6 text-center hover:shadow-lg transition">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Verified Certifications</h3>
                <p className="text-gray-600 text-sm">Get recognized certificates that boost your profile and career prospects</p>
                <button className="mt-4 text-blue-600 text-sm font-medium hover:underline">Learn More</button>
              </div>
              <div className="bg-white rounded-xl border-2 border-gray-200 p-6 text-center hover:shadow-lg transition">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Placement Support</h3>
                <p className="text-gray-600 text-sm">Connect with hiring partners and get interview preparation assistance</p>
                <button className="mt-4 text-blue-600 text-sm font-medium hover:underline">Learn More</button>
              </div>
            </div>
            
            {/* Stats Section */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-6 mt-12">
              <div className="text-center">
                <AnimatedCounter end={500} suffix="+" />
                <div className="text-sm text-gray-600 mt-1">Courses Available</div>
              </div>
              <div className="text-center">
                <AnimatedCounter end={85} suffix="%" />
                <div className="text-sm text-gray-600 mt-1">Course Completion</div>
              </div>
              <div className="text-center">
                <AnimatedCounter end={2} suffix="K+" />
                <div className="text-sm text-gray-600 mt-1">Active Learners</div>
              </div>
              <div className="text-center">
                <AnimatedCounter end={92} suffix="%" />
                <div className="text-sm text-gray-600 mt-1">Learner Satisfaction</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-blue-600">24/7</div>
                <div className="text-sm text-gray-600 mt-1">AI Support</div>
              </div>
              <div className="text-center">
                <AnimatedCounter end={150} suffix="+" />
                <div className="text-sm text-gray-600 mt-1">Hiring Partners</div>
              </div>
            </div>
          </div>
        </section>

        {/* Learn from Companies Section */}
        <section className="bg-white py-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <h2 className="text-center text-3xl font-bold text-gray-900 mb-4">
              Learn from top companies and get placed
            </h2>
            <p className="text-center text-gray-600 mb-12">
              Industry-aligned curriculum from leading organizations
            </p>
            <div className="flex flex-wrap items-center justify-center gap-12">
              {companyLogos.map((company, index) => (
                <div key={index} className="hover:grayscale transition opacity-100 hover:opacity-60">
                  <img src={company.logo} alt={company.name} className="h-12 object-contain" />
                </div>
              ))}
            </div>
          </div>
        </section>
        {/* Search Section */}
        <section className="bg-gray-50 py-12">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <h2 className="text-center text-3xl font-bold text-gray-900 mb-8">
              Search learning programs
            </h2>
            <div className="mx-auto max-w-3xl">
              <div className="relative">
                <Input
                  placeholder="e.g. Machine Learning, Data Science, Web Development"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-14 pl-12 text-lg border-2 border-gray-300 rounded-lg"
                />
                <svg className="absolute left-4 top-4 h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                {categoryFilters.map((label) => (
                  <button
                    key={label}
                    onClick={() => setActiveCategory(label)}
                    className={`rounded-full border-2 px-5 py-2 text-sm font-medium transition ${
                      activeCategory === label
                        ? 'border-blue-600 bg-blue-600 text-white'
                        : 'border-gray-300 bg-white text-gray-700 hover:border-blue-600 hover:text-blue-600'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* All Courses Section */}
        <section className="bg-white py-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">
              Explore our courses
            </h2>
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading courses...</p>
              </div>
            ) : (
              <>
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {filteredCourses.slice(0, 6).map((course) => (
                    <div
                      key={course.id}
                      className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer group"
                      onClick={() => navigate(`/courses/${course.id}`)}
                    >
                      {/* Course Image */}
                      <div className="relative h-48 overflow-hidden bg-gray-100">
                        <img 
                          src={course.image} 
                          alt={course.title} 
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute top-3 left-3">
                          <span className="inline-block bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-semibold">
                            {course.category}
                          </span>
                        </div>
                      </div>
                      
                      {/* Course Content */}
                      <div className="p-5">
                        <p className="text-xs text-gray-500 mb-2">{course.provider}</p>
                        <h3 className="text-lg font-bold text-gray-900 mb-3 line-clamp-2 min-h-[3.5rem]">{course.title}</h3>
                        
                        <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                          <div className="flex items-center gap-1">
                            <svg className="w-4 h-4 text-yellow-500 fill-current" viewBox="0 0 20 20">
                              <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
                            </svg>
                            <span className="font-medium">{course.rating}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>{course.duration}</span>
                          </div>
                          <span className="bg-gray-100 px-2 py-1 rounded text-xs font-medium">{course.level}</span>
                        </div>
                        
                        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                          <div className="flex flex-col">
                            <div className="flex items-center gap-2">
                              <p className="text-2xl font-bold text-gray-900">{course.price}</p>
                              {course.original_price && course.original_price > parseFloat(course.price.replace(/[^0-9.]/g, '')) && (
                                <span className="text-sm text-gray-500 line-through">
                                  ₹{course.original_price.toLocaleString('en-IN')}
                                </span>
                              )}
                            </div>
                            {course.original_price && course.original_price > parseFloat(course.price.replace(/[^0-9.]/g, '')) && (
                              <span className="text-xs text-green-600 font-semibold">
                                {Math.round(((course.original_price - parseFloat(course.price.replace(/[^0-9.]/g, ''))) / course.original_price) * 100)}% OFF
                              </span>
                            )}
                          </div>
                          <Button 
                            size="sm"
                            className="bg-gray-900 hover:bg-gray-800 text-white px-4 py-2 rounded-lg text-sm font-medium"
                            onClick={(e) => { e.stopPropagation(); handleEnroll(course); }}
                          >
                            {isLoggedIn() && role === 'candidate' && isEnrolled(course.id) ? 'Continue' : 'Enroll'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {filteredCourses.length > 6 && (
                  <div className="text-center mt-10">
                    <Button 
                      variant="outline" 
                      className="px-8"
                      onClick={() => {
                        navigate('/all-courses');
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                    >
                      View All Courses
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="bg-gray-50 py-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <h2 className="text-center text-3xl font-bold text-gray-900 mb-12">
              What learners are achieving with Aptirix
            </h2>
            <div className="grid gap-6 md:grid-cols-3">
              {testimonials.map((testimonial, index) => (
                <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition">
                  <div className="flex items-start gap-4 mb-4">
                    <div className={`${testimonial.bgColor} h-12 w-12 rounded-full flex items-center justify-center flex-shrink-0`}>
                      <span className="text-white text-xl font-bold">{testimonial.initial}</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 text-lg">{testimonial.name}</h4>
                      <p className="text-sm text-gray-500">{testimonial.role}</p>
                    </div>
                  </div>
                  <p className="text-gray-600 leading-relaxed text-sm">{testimonial.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA - hidden after free trial used */}
        {!hasUsedFreeTrial && <section className="relative bg-gradient-to-r from-blue-600 to-purple-600 py-20 text-white overflow-hidden">
          {/* Background Image with Overlay */}
          <div className="absolute inset-0 z-0">
            <img 
              src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=1600&h=400&fit=crop" 
              alt="Learning" 
              className="w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600/90 to-purple-600/90"></div>
          </div>
          
          {/* Content */}
          <div className="relative z-10 mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
            <h2 className="text-4xl font-bold mb-4">Start your learning journey today</h2>
            <p className="text-xl mb-8 text-white/90">
              Join Indian professionals upskilling for better career opportunities
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button 
                className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-3 text-lg rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all"
                onClick={() => { if (requireAuth()) { navigate('/course-preview'); } }}
              >
                Start Free Trial
              </Button>
              <Button 
                className="bg-transparent text-white border-2 border-white hover:bg-white/10 px-8 py-3 text-lg rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all"
                onClick={() => navigate('/all-courses')}
              >
                View Courses
              </Button>
            </div>
          </div>
        </section>}
      </main>
      
      <Footer />
    </div>
  );
};

export default CoursesPage;
